import { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ResourceCard from '@/components/base/ResourceCard';
import { getCategoryBySlug } from '@/mocks/categories';
import { getResourcesByCategory, resources } from '@/mocks/resources';

export default function CategoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeTag, setActiveTag] = useState<string>('全部');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const category = getCategoryBySlug(slug || '');
  const categoryResources = category ? getResourcesByCategory(category.id) : [];

  const allTags = ['全部', ...Array.from(new Set(categoryResources.flatMap((r) => r.tags)))];

  const filteredResources = activeTag === '全部'
    ? categoryResources
    : categoryResources.filter((r) => r.tags.includes(activeTag));

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">分类不存在</h1>
            <p className="text-foreground-500 text-sm mb-6">未找到该分类，请检查链接是否正确</p>
            <Link to="/categories" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回全部分类 →
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/categories" className="hover:text-primary-600 transition-colors">全部分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">{category.name}</span>
            </nav>

            <div className="mb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">
                    {category.description} · 共 {categoryResources.length} 个资源
                  </p>
                </div>
              </div>
            </div>

            {allTags.length > 1 && (
              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTag === tag
                        ? 'bg-primary-500 text-white'
                        : 'bg-secondary-50 text-secondary-600 hover:bg-secondary-100'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredResources.map((resource) => (
                <ResourceCard key={resource.id} {...resource} />
              ))}
            </div>

            {filteredResources.length === 0 && (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-xl text-secondary-600"></i>
                </div>
                <p className="text-foreground-500 text-sm">该标签下暂无资源</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}